/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { CdkVirtualScrollViewport, VirtualScrollStrategy, VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import { Directive, forwardRef, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ItemProfile } from '../types/item-profile';

export class VirtualGridScrollStrategy implements VirtualScrollStrategy {
  private $scrolledIndexChange = new Subject<number>();
  scrolledIndexChange: Observable<number> = this.$scrolledIndexChange.pipe(distinctUntilChanged());

  private viewport: CdkVirtualScrollViewport | null = null;

  constructor(
    private itemsProfiles: Array<ItemProfile>,
    private minBufferPx: number,
    private maxBufferPx: number,
    private error: EventEmitter<{ message: any; error: any }>
  ) {}

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.updateTotalCountSize();
    this.updateRenderedRange();
  }

  detach(): void {
    this.$scrolledIndexChange.complete();
    this.viewport = null;
  }

  updateSizes(itemsProfiles: Array<ItemProfile>, minBufferPx: number, maxBufferPx: number) {
    if (maxBufferPx < minBufferPx) {
      throw Error('Virtual Grid scroll: maxBufferPx must be greater than or equal to minBufferPx');
    }

    this.itemsProfiles = itemsProfiles;
    this.minBufferPx = minBufferPx;
    this.maxBufferPx = maxBufferPx;

    this.updateTotalCountSize();
    this.updateRenderedRange();
  }

  onContentScrolled(): void {
    this.updateRenderedRange();
  }

  onDataLengthChanged(): void {
    this.updateTotalCountSize();
    this.updateRenderedRange();
  }

  onContentRendered(): void {
    /** no-op */
  }

  onRenderedOffsetChanged(): void {
    /** no-op */
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    try {
      if (!index) {
        this.viewport.scrollToOffset(0, behavior);
      } else if (this.viewport && this.itemsProfiles[index]?.position >= 0) {
        this.viewport.scrollToOffset(this.itemsProfiles[index].position, behavior);
      }
    } catch (error) {
      const message = `VirtualGridScrollStrategy.scrollToIndex(${index}, ${behavior})`;
      this.error.emit({ message, error });
      console.error(message, error);
    }
  }

  private profileBottom(profile: ItemProfile): number {
    return profile.position + profile.height;
  }

  private firstVisibleIndex = 0;

  private indexOfOffset(firstVisibleIndex: number, offset: number): number {
    if (!this.itemsProfiles || !this.itemsProfiles.length || !this.itemsProfiles[firstVisibleIndex]) return 0;

    const direction = offset < this.itemsProfiles[firstVisibleIndex].position ? -1 : 1;

    for (
      let i = firstVisibleIndex;
      i < this.itemsProfiles.length &&
      i >= 0 &&
      ((direction < 0 && this.itemsProfiles[i].position > offset) ||
        (direction > 0 && this.itemsProfiles[i].position < offset));
      i += direction
    ) {
      firstVisibleIndex = i;
    }

    return firstVisibleIndex;
  }

  private updateTotalCountSize() {
    try {
      if (!this.viewport || !this.itemsProfiles || !this.itemsProfiles.length) return;
      const last = this.itemsProfiles[this.itemsProfiles.length - 1];
      this.viewport.setTotalContentSize(last.position + last.height);
    } catch (error) {
      const message = `VirtualGridScrollStrategy.updateTotalCountSize()`;
      this.error.emit({ message, error });
      console.error(message, error);
    }
  }

  private updateRenderedRange() {
    try {
      if (!this.viewport) return;

      if (!this.itemsProfiles || !this.itemsProfiles.length) {
        this.viewport.setRenderedRange({ start: 0, end: 0 });
        this.viewport.setRenderedContentOffset(0);
        this.$scrolledIndexChange.next(0);
        return;
      }

      const renderedRange = this.viewport.getRenderedRange();
      const newRange = { start: renderedRange.start, end: renderedRange.end };
      const viewportSize = this.viewport.getViewportSize() || this.viewport.elementRef.nativeElement.clientHeight;
      const dataLength = this.itemsProfiles.length;
      const scrollOffset = this.viewport.measureScrollOffset();
      const firstVisibleIndex = (this.firstVisibleIndex = this.indexOfOffset(this.firstVisibleIndex, scrollOffset));

      // If user scrolls to the bottom of the list and data changes to a smaller list
      if (renderedRange.end > dataLength) {
        newRange.start = firstVisibleIndex;
        newRange.end = dataLength;
      }

      const startBuffer = scrollOffset - this.itemsProfiles[newRange.start].position;

      if (startBuffer < this.minBufferPx && newRange.start != 0) {
        const expandStartPx = scrollOffset - this.maxBufferPx;
        while (this.itemsProfiles[newRange.start]?.position >= expandStartPx && newRange.start >= 0) {
          newRange.start--;
        }
        newRange.start++;
      } else if (startBuffer > this.maxBufferPx) {
        const expandStartPx = scrollOffset - this.maxBufferPx;
        while (this.itemsProfiles[newRange.start]?.position < expandStartPx) {
          newRange.start++;
        }
        newRange.start = newRange.start >= dataLength ? dataLength - 1 : newRange.start;
      }

      newRange.end = newRange.end ? newRange.end : 1;
      const endBuffer = this.profileBottom(this.itemsProfiles[newRange.end - 1]) - (scrollOffset + viewportSize);

      if (endBuffer < this.minBufferPx && newRange.end != dataLength) {
        const expandEndPx = scrollOffset + viewportSize + this.maxBufferPx;
        while (
          this.itemsProfiles[newRange.end - 1] &&
          this.profileBottom(this.itemsProfiles[newRange.end - 1]) <= expandEndPx &&
          newRange.end <= dataLength
        ) {
          newRange.end++;
        }
        newRange.end--;
      } else if (endBuffer > this.maxBufferPx) {
        const expandEndPx = scrollOffset + viewportSize + this.maxBufferPx;
        while (this.profileBottom(this.itemsProfiles[newRange.end - 1]) > expandEndPx) {
          newRange.end--;
        }
      }

      this.viewport.setRenderedRange(newRange);
      this.viewport.setRenderedContentOffset(this.itemsProfiles[newRange.start].position);
      this.$scrolledIndexChange.next(Math.floor(firstVisibleIndex));
    } catch (error) {
      const message = `VirtualGridScrollStrategy.updateRenderedRange()`;
      this.error.emit({ message, error });
      console.error(message, error);
    }
  }
}

export function virtualGridScrollStrategyFactory(directive: VirtualGridScroll) {
  return directive.scrollStrategy;
}

@Directive({
  selector: 'cdk-virtual-scroll-viewport[itemsProfiles]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: virtualGridScrollStrategyFactory,
      deps: [forwardRef(() => VirtualGridScroll)]
    }
  ]
})
export class VirtualGridScroll implements OnChanges {
  @Input()
  itemsProfiles: Array<ItemProfile> = [];

  @Input()
  get minBufferPx(): number {
    return this.$minBufferPx;
  }
  set minBufferPx(size: number) {
    this.$minBufferPx = coerceNumberProperty(size);
  }
  $minBufferPx = 150;

  @Input()
  get maxBufferPx(): number {
    return this.$maxBufferPx;
  }
  set maxBufferPx(size: number) {
    this.$maxBufferPx = coerceNumberProperty(size);
  }
  $maxBufferPx = 300;

  @Output()
  error = new EventEmitter<{ message: any; error: any }>();

  scrollStrategy: VirtualGridScrollStrategy;

  constructor() {
    this.scrollStrategy = new VirtualGridScrollStrategy(
      this.itemsProfiles,
      this.minBufferPx,
      this.maxBufferPx,
      this.error
    );
  }

  ngOnChanges() {
    this.scrollStrategy.updateSizes(this.itemsProfiles, this.minBufferPx, this.maxBufferPx);
  }

  static ngAcceptInputType_rowSize: NumberInput;
  static ngAcceptInputType_groupSize: NumberInput;
  static ngAcceptInputType_minBufferPx: NumberInput;
  static ngAcceptInputType_maxBufferPx: NumberInput;
}
